import {
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";

import { IncidentService } from "@app/incident/services/incident.service";
import { downloadWithLink } from "@methods/CommonMethods";
import { CerrixDocumentsListComponent } from "../../../shared/cerrix-documents/cerrix-documents-list/cerrix-documents-list.component";
import { DocumentModel } from "../../../shared/cerrix-documents/models/document.model";
import { BaseDocumentsService } from "../../../shared/cerrix-documents/services/base-documents.service";
import { map, Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatTabsModule } from "@angular/material/tabs";
import { CerrixHyperlinksListComponent } from "@app/shared/cerrix-hyperlinks/cerrix-hyperlinks-list/cerrix-hyperlinks-list.component";
import { IncidentHyperlinkModel } from "@app/shared/cerrix-hyperlinks/models/hyperlink-model";
import { CerrixButtonComponent } from "../../components/src/lib/core/cerrix-button/cerrix-button.component";

@Component({
    selector: "incident-attachments-tab",
    templateUrl: "./incident-attachments-tab.component.html",
    styleUrls: ["./incident-attachments-tab.component.scss"],
    imports: [
        CerrixDocumentsListComponent,
        MatTabsModule,
        CerrixButtonComponent,
        CerrixHyperlinksListComponent,
    ],
    providers: [],
})
export class IncidentAttachmentsTabComponent implements OnInit {
    @Input() canEditAttachments!: boolean;

    @Input() public incidentId: string;

    @Input() public documents: DocumentModel[];

    @Output() documentsChange = new EventEmitter<DocumentModel[]>();

    @Input() public hyperlinks: IncidentHyperlinkModel[];
    @Output() hyperlinksChange = new EventEmitter<IncidentHyperlinkModel[]>();

    @ViewChild("btnAttachment") btnAttachment: CerrixButtonComponent;
    @ViewChild("btnHyperlinks") btnHyperlinks: CerrixButtonComponent;

    private destroyRef = inject(DestroyRef);
    private incidentService = inject(IncidentService);
    private documentsService = inject(BaseDocumentsService);

    showDocuments = true;
    showHyperlinks = false;

    documentCount: string = "";
    hyperlinkCount: string = "";

    ngOnInit() {
        this.setButtonTexts();
    }

    protected onRemoveFile(document: DocumentModel) {
        this.documents = this.documents.filter((d) => d !== document);
        this.documentsChange.emit(this.documents);
    }

    protected onFileDownload(document: DocumentModel) {
        this.getDocumentLocalUrl(document)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((localUrl) => {
                downloadWithLink(localUrl, document.name);
            });
    }

    protected onOpenDocumentViewer(document: DocumentModel) {
        this.getDocumentLocalUrl(document)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((localUrl) => {
                this.documentsService.openDocumentViewer(document.id, document.name, localUrl);
            });
    }

    protected onDocumentsChange(documents: DocumentModel[]) {
        this.documents = documents;
        this.setButtonTexts();
        this.documentsChange.emit(this.documents);
    }

    protected onHyperlinksChange(hyperlinks: IncidentHyperlinkModel[]) {
        this.hyperlinks = hyperlinks;
        this.setButtonTexts();
        this.hyperlinksChange.emit(this.hyperlinks);
    }

    private setButtonTexts() {
        this.documentCount =
            this.documents && this.documents.length > 0
                ? `(${this.documents.length.toString()})`
                : "";
        this.hyperlinkCount =
            this.hyperlinks && this.hyperlinks.length > 0
                ? `(${this.hyperlinks.length.toString()})`
                : "";
    }

    private getDocumentLocalUrl(document: DocumentModel): Observable<string> {
        if (document.id && !document.file) {
            return this.incidentService
                .downloadDocument(this.incidentId, document.id, document.documentId)
                .pipe(map((data) => URL.createObjectURL(data)));
        } else {
            const localUrl = URL.createObjectURL(document.file);

            return new Observable<string>((obs) => {
                obs.next(localUrl);
                obs.complete();
            });
        }
    }

    onAttachmentsClick() {
        this.showDocuments = true;
        this.showHyperlinks = false;

        this.btnAttachment.color = "primary-light";
        this.btnHyperlinks.color = "accent";
    }

    onHyperlinksClick() {
        this.btnAttachment.color = "accent";
        this.btnHyperlinks.color = "primary-light";

        this.showDocuments = false;
        this.showHyperlinks = true;
    }
}
